import type { ContentSaveBody, SavableForecast } from "../../types/persistence.js";

/**
 * Project a forecast onto the content-save blob for `POST /api/forecasts`.
 *
 * Two classes of field are dropped:
 *
 * - **Client-only flags** — wire-irrelevant, dropped so they don't churn the
 *   save-effect snapshot diff or survive reload:
 *   - `_debug`: client-only LLM trace, changes on every server response.
 *   - `_pending`: optimistic-placeholder marker; persisting it would leave a
 *     completed row pretending to still be in-flight after reload.
 *   - `_series` is slimmed to `{name: {color, ssid?}}` pairs — the data array /
 *     yMin / yMax / unit / sourceUrl all re-derive from `/api/series/:name` on load
 *     (`fetchLiveSeries`), so persisting them is dead weight and would also trip
 *     the save-effect's snapshot diff on every live refresh.
 *
 * - **PATCH-only fields** ({@link ForecastWire} schedule / contractStatus /
 *   notify / seriesImpacts) — server-managed / chatboard-local, each edited via
 *   its own dedicated `PATCH /api/forecasts/:id` path. The server upsert guard
 *   400s a POST carrying ANY of them. `selectLegacyForecasts` always projects
 *   `contractStatus` (as null when absent), so without this strip every
 *   content-save POST would carry `"contractStatus":null` and 400, because
 *   null !== undefined trips the guard.
 *
 * The `: ContentSaveBody` return annotation is the build-time guard: because
 * that type forbids every PATCH-only field (`?: never`), forgetting to strip one
 * leaves it in `rest` and fails to compile, so the mismatch is a tsc error, not a
 * runtime 400.
 */
export const stripDebugForWire = (f: SavableForecast): ContentSaveBody => {
  const {
    _debug,
    _pending,
    detailPending,
    detailLocal,
    detailLocalFields,
    forkDerived,
    contractQuestion,
    listSummary,
    _series,
    schedule,
    contractStatus,
    notify,
    seriesImpacts,
    reportPointers,
    ...rest
  } = f;
  void _debug;
  void _pending;
  // Client-only "lean row, detail backfill still owed" marker. Keeping it out of the snapshot
  // keeps a row's saved bytes identical before and after the backfill flips the flag; otherwise
  // every boot-hydrated row would look changed and re-POST itself.
  void detailPending;
  // Same treatment for the provenance markers: client-only, and keeping them out of the
  // snapshot means a row's saved bytes do not change just because the backfill landed.
  void detailLocal;
  void detailLocalFields;
  void forkDerived;
  // Read-only: the server derives it from `contract.question`, which the body already
  // carries — and a copy saved back would go stale the moment the question is edited.
  void contractQuestion;
  // Read-only too: the lean list derives it from `contract` / `history`, which the body carries.
  void listSummary;
  void schedule;
  void contractStatus;
  void notify;
  void seriesImpacts;
  // Server-owned: only the server appends pointers, and the upsert ignores the field. It must
  // also stay out of the SNAPSHOT this function defines:
  // the read-through merge adds pointers to completed runs, and a field in the snapshot turns
  // every such merge into a full-content re-POST over the server's row.
  void reportPointers;
  // Server-owned too: the resolver poll and the notifier write `serverContext.resolution`, and
  // the upsert prefers a sent one over the stored. A tab's copy is the one it loaded, so sending
  // it back could replace a newer check, or a verdict, with an older result.
  if (rest.serverContext && typeof rest.serverContext === "object") {
    const { resolution: _stored, ...ctx } = rest.serverContext as Record<string, unknown>;
    void _stored;
    rest.serverContext = ctx as typeof rest.serverContext;
  }
  if (_series && typeof _series === "object") {
    const slim: Record<string, { color: unknown; ssid?: string }> = {};
    for (const [name, v] of Object.entries(_series)) {
      if (v && typeof v === "object" && "color" in v && (v as { color?: unknown }).color) {
        slim[name] = { color: (v as { color: unknown }).color };
        // The signals-service id is kept even though it is not re-derivable — it is
        // the OPPOSITE of dead weight. A suggestion's `name` is an agent-authored
        // display label ("QUAL closing price"), which the legacy name resolver
        // cannot resolve, so a name-only reload 404s on a signal that is perfectly
        // alive in the catalog. The id is the only durable handle back to it.
        // Omitted (not null) when absent so a legacy series' saved blob is
        // byte-identical to before and doesn't churn the save-effect diff.
        const ssid = (v as { ssid?: unknown }).ssid;
        if (typeof ssid === "string" && ssid) slim[name].ssid = ssid;
      }
    }
    return { ...rest, _series: slim };
  }
  return rest;
};
